import React, { useEffect, useMemo } from 'react';
import { demeterMarketIndicatorsApi } from '../../../Apis/Apis';
import { DemeterFilterTimeSpan } from '../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import useCacheThenApi from '../../Apis/Hooks/useCacheThenApiHook';
import MarketIndicatorChart from '../../Components/Charts/MarketIndicator/MarketIndicatorChart';
import MarketIndicatorForecastChart from '../../Components/Charts/MarketIndicators/MarketIndicatorForecast/MarketIndicatorForecastChart';
import PageHeaderWithTooltip from '../../Components/Headers/PageHeaderWithTooltip';
import MenuByMarketIndicatorNavigation from '../../Components/MenuFileSelectors/MenuByMarketIndicatorNavigation';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import CacheKeys from '../../Services/Cache/CacheKeys';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './HedgeMonitorProPage.module.scss';
import HedgeMonitorProSummary from './HedgeMonitorProSummary';
import HedgeMonitorProTable from './HedgeMonitorProTable';

const HedgeMonitorProPage: React.FC = () => {
    // Application hooks and application constants.
    const [translations, translate] = useLanguage();
    const [searchParameters] = useSearchParameters();

    const [, , listMarketIndicatorsResponse] = useCacheThenApi(CacheKeys.ListMarketIndicatorsCurrent, () =>
        demeterMarketIndicatorsApi.listMarketIndicatorsCurrent(),
    );

    const [, refreshMarketIndicatorByRegionCommodity, getMarketIndicatorByRegionCommodityResponse] = useApiWithoutAutoExecute(() => {
        const { region, subRegion, commodity, dataSource } = listMarketIndicatorsResponse!.rows!.find(
            (x) => x.marketIndicatorGuid === searchParameters.marketIndicatorGuid,
        )!;

        return demeterMarketIndicatorsApi.getMarketIndicatorByRegionAndCommodity(region, commodity, subRegion ?? undefined, dataSource ?? undefined);
    });

    const marketIndicator = useMemo(() => getMarketIndicatorByRegionCommodityResponse?.marketIndicator, [getMarketIndicatorByRegionCommodityResponse]);

    const [isForecastLoading, refreshFuturesForecast, futuresForecastResponse, futuresForecastError] = useApiWithoutAutoExecute(() => {
        const { region, commodity } = listMarketIndicatorsResponse!.rows!.find((x) => x.marketIndicatorGuid === searchParameters.marketIndicatorGuid)!;

        return demeterMarketIndicatorsApi.listFuturesForecast(region, commodity);
    });

    useEffect(() => {
        if (!listMarketIndicatorsResponse || listMarketIndicatorsResponse.rows?.length === 0 || !searchParameters.marketIndicatorGuid) {
            return;
        }

        refreshMarketIndicatorByRegionCommodity();
        refreshFuturesForecast();
    }, [searchParameters.marketIndicatorGuid, listMarketIndicatorsResponse]);

    const chartTitleString = useMemo(
        () => `${translate(marketIndicator?.displayName ?? '')} ${translations.words.budget} ${translations.words.projection}`,
        [translations, marketIndicator],
    );

    return (
        <div className={styles.master_page_container} data-testid="HedgeMonitorProPage">
            <PageHeaderWithTooltip
                title={translations.hedgeMonitorPro.title}
                testId="HedgeMonitorProPageHeader"
                tooltip={translations.hedgeMonitorPro.tooltipMessage}
            />
            {listMarketIndicatorsResponse && (
                <MenuByMarketIndicatorNavigation
                    isHedgeMonitorPro
                    listMarketIndicatorsResponse={listMarketIndicatorsResponse}
                    track={{ eventCategory: 'HedgeMonitorPro', eventAction: 'Menu' }}
                />
            )}
            <div className={styles.hedge_monitor_pro_halves} data-testid="HedgeMonitorProPageContent">
                <div className={styles.hedge_monitor_pro_summary}>
                    <div className={styles.hedge_monitor_pro_summary_margin}>
                        <HedgeMonitorProSummary marketIndicator={marketIndicator} track={{ eventCategory: 'HedgeMonitorPro', eventAction: 'Summary' }} />
                    </div>
                    <HedgeMonitorProTable marketIndicator={marketIndicator} />
                </div>
                <div className={styles.hedge_monitor_pro_market_charts}>
                    {!futuresForecastError && (
                        <div className={styles.hedge_monitor_pro_market_chart}>
                            <MarketIndicatorForecastChart
                                title={`${chartTitleString}`}
                                runTestMarketIndicatorFactorResponse={futuresForecastResponse}
                                yAxisLabel={`${marketIndicator?.currency} / ${marketIndicator?.unitOfMeasure}`}
                            />
                        </div>
                    )}
                    <div className={styles.hedge_monitor_pro_market_chart}>
                        <MarketIndicatorChart
                            useColorPalletteInLegend
                            useHedgeMonitorProRows
                            runMarketIndicatorResponse={getMarketIndicatorByRegionCommodityResponse}
                            chartNavigatorTimeRange={DemeterFilterTimeSpan.ThreeMonths}
                            testId="MarketIndicatorChart"
                            track={{ eventCategory: 'HedgeMonitorPro', eventAction: 'HedgeMonitorProChart' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HedgeMonitorProPage;
